import gsap from "gsap";

import { Basics, isTouch } from "../_app/cuchillo/core/Basics";
import { GetBy } from "../_app/cuchillo/core/Element";
import { Metrics } from "../_app/cuchillo/core/Metrics";
import { Scroll } from "../_app/cuchillo/scroll/Scroll";
import { Maths } from "../_app/cuchillo/utils/Maths";

const PIXEL_STEP = 300;

export default class ImagesCursor {
    _container;
    _holder;
    _items = [];
    _calls = {
        start: (e) => this.start(e),
        move: (e) => this.move(e),
        hide: () => this.hide()
    }
    currentIndex = 0;
    _positions = {
        target: { x: 0, y: 0 },
        mouse: { x: 0, y: 0 },
        last: { x: 0, y: 0 }
    }
    center = true;

    constructor(__container = document.body, __holder = document.body, __class = ImageCursor) {
        this._container = __container;
        this._holder = __holder;
        this._holder.addEventListener(Basics.mouseOver, this._calls.start);
        document.addEventListener(Basics.moveEvent, this._calls.move);

        const imgs = GetBy.class('__img', this._container);
        for (let i = 0; i < imgs.length; i++) {
            this._items.push(new __class(imgs[i], i, imgs.length));
        }
    }

    show() { }

    start(e) {
        this.center = false;

        this._positions.mouse = this._positions.last = {
            x: isTouch ? e.touches[0].screenX : e.clientX,
            y: isTouch ? e.touches[0].screenY : e.clientY,
        };
        this._positions.last = this._positions.mouse;
        this.loop();
    }

    move(e) {
        this._positions.mouse = {
            x: isTouch ? e.touches[0].screenX : e.clientX,
            y: isTouch ? e.touches[0].screenY : e.clientY,
        };
    }

    hide() {
        this.center = true;
        this._items.map(item => item.hide());
    }

    calculateIndex() {
        const displacementX = this._positions.target.x - this._positions.last.x;
        const displacementY = this._positions.target.y - this._positions.last.y;
        const displacement = Math.sqrt(displacementX ** 2 + displacementY ** 2);

        if (displacement >= PIXEL_STEP) {
            this.currentIndex = (this.currentIndex + 1) % this._items.length;
            this._positions.last.x = this._positions.target.x;
            this._positions.last.y = this._positions.target.y;
        }
    }

    loop() {
        if (this.center) {
            this._positions.target.x = Metrics.WIDTH / 2;
            this._positions.target.y = Metrics.HEIGHT / 2;
        } else {
            this._positions.target = this._positions.mouse;
        }

        this.calculateIndex();

        this._items.map(item => item.loop({ x: this._positions.target.x, y: this._positions.target.y }, this.currentIndex));
    }

    resize() {
        this._items.map(item => item.resize());
    }

    dispose() {
        document.removeEventListener(Basics.moveEvent, this._calls.move);
        this._holder.removeEventListener(Basics.mouseOver, this._calls.show);

        this._container = null;
        this._items = [];
    }
}

export const LERPS = [.028, .045, .082];
export const OPA = [1, .5, 0];

export class ImageCursor {
    _container;
    _img;
    _figure;
    _overlay;
    _visible = false;
    _width = 0;
    _height = 0;
    _sizes = {
        container: { x: 0, y: 0 },
        img: { x: 0, y: 0 },
        diff: { x: 0, y: 0 },
    };
    _speed = { x: 0, y: 0 };
    _positions = {
        current: { x: 0, y: 0 },
        target: { x: 0, y: 0 },
        center: { x: 0, y: 0 },
        old: { x: 0, y: 0 },
        displace: { x: 0, y: 0 }
    }
    _rotations = {
        current: 0,
        target: 0
    }
    _index;
    _total;
    _current = -1;
    _factor = LERPS[0];
    _opacity = 1;
    _item_visible = 3;

    constructor(__container, __i, __total) {
        this._index = __i;
        this._total = __total;
        this._container = __container;
        this._img = GetBy.selector('img', __container)[0];
        this._figure = GetBy.selector('figure', __container)[0];
        this._overlay = GetBy.class('overlay', __container)[0];

        this.directHide();
    }

    show() {
        gsap.killTweensOf(this._overlay);
        gsap.to(this._overlay, { opacity: this._opacity, duration: .1 });

        if (this._visible) return;

        this._visible = true;

        this._positions.current.x = this._positions.target.x;
        this._positions.current.y = this._positions.target.y;

        gsap.killTweensOf(this._img);
        gsap.to(this._img, { opacity: 1, duration: .1 });
        gsap.to(this._container, { opacity: 1, duration: .5 });
    }

    directHide() {
        gsap.killTweensOf(this._overlay);
        gsap.killTweensOf(this._img);

        this._visible = false;
        this._img.style.opacity = 0;
        this._factor = 1;
        this._opacity = this._overlay.style.opacity = 0;
        this._container.style.zIndex = 0;
        this._container.style.opacity = 0;
    }

    hide() {
        if (!this._visible) return;

        this._visible = false;

        gsap.killTweensOf(this._overlay);
        gsap.killTweensOf(this._img);

        gsap.to(this._img, {
            opacity: 0,
            duration: .2,
            onComplete: () => {
                this._factor = LERPS[0];
                this._container.style.zIndex = 0;
            }
        });
        gsap.to(this._overlay, {
            opacity: 0,
            duration: .2,
            onComplete: () => {
                this._opacity = 0;
                this._container.style.opacity = 0;
            }
        });
    }

    switchOrder() {
        const relativeIndex = (this._index - this._current + this._total) % this._total;

        if (relativeIndex < this._item_visible) {
            this._factor = LERPS[relativeIndex];
            this._opacity = OPA[relativeIndex];
            this._container.style.zIndex = relativeIndex;

            this.show();
        } else {
            this.hide();
        }
    }

    loop({ x, y }, __current) {
        // Positions
        this._positions.target.x = x - this._positions.center.x;
        this._positions.target.y = y - this._positions.center.y - Scroll.y;

        this._positions.current.x = Maths.lerp(this._positions.current.x, this._positions.target.x, this._factor);
        this._positions.current.y = Maths.lerp(this._positions.current.y, this._positions.target.y, this._factor);

        this._speed.x = this._positions.current.x - this._positions.old.x;
        this._speed.y = this._positions.current.y - this._positions.old.y;

        this._positions.old.x = this._positions.current.x;
        this._positions.old.y = this._positions.current.y;

        // Rotations
        this._rotations.target = Maths.clamp(this._speed.x * .3, -25, 25);
        this._rotations.current = Maths.lerp(this._rotations.current, this._rotations.target, .07);

        this._container.style.transform = `translate(${this._positions.current.x}px, ${this._positions.current.y}px) rotateZ(${this._rotations.current}deg)`;

        const posX = (x - Metrics.CENTER_X) / Metrics.WIDTH;
        const posY = (y - Metrics.CENTER_Y) / Metrics.HEIGHT;
        // const displX = Maths.clamp(Maths.lerp(0, this._sizes.diff.x, posX), -this._sizes.diff.x, this._sizes.diff.x);
        // const displY = Maths.clamp(Maths.lerp(0, this._sizes.diff.y, posY), -this._sizes.diff.y, this._sizes.diff.y);
        const displX = Maths.lerp(0, this._sizes.diff.x, posX);
        const displY = Maths.lerp(0, this._sizes.diff.y, posY);
        this._positions.displace.x = Maths.lerp(this._positions.displace.x, displX, .48);
        this._positions.displace.y = Maths.lerp(this._positions.displace.y, displY, .48);

        this._figure.style.transform = `translate(${this._positions.displace.x}px, ${this._positions.displace.y}px)`;

        if (__current !== this._current) {
            this._current = __current;
            this.switchOrder();
        }
    }

    resize() {
        const { width, height } = this._container.getBoundingClientRect();
        const { width: imgWidth, height: imgHeight } = this._img.getBoundingClientRect();

        this._sizes.container.x = width;
        this._sizes.container.y = height;
        this._sizes.img.x = imgWidth;
        this._sizes.img.y = imgHeight;
        this._sizes.diff.x = imgWidth - width;
        this._sizes.diff.y = imgHeight - height;

        this._positions.center.x = width / 2;
        this._positions.center.y = height / 2;

        this._positions.current.x = this._positions.target.x;
        this._positions.current.y = this._positions.target.y;
    }

    dispose() {
        this.hide();
    }
}
